import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'api/axios.config'
import { useRecoilCallback, useSetRecoilState } from 'recoil'
import type { RecoilState } from 'recoil'
import type { AxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { caseFuneralHomeAtom } from '@/atoms/globalAtoms'
import { selectedThemeAtom } from '@/atoms/printPackageAtoms'
import { fhLogoAtom } from '@/atoms/editorAtoms'
import {
  pageSetLocaleAtom,
  serviceInformationTemplatesAtom,
} from '@/atoms/_editorAtoms'
import { caseSelector } from '@/atoms/caseAtoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { editorPageContentAtomFamily } from '@/atoms/editorTextBoxAtoms'
import type { EditorPageContentBundle } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { dateFormatAtom } from '@/atoms/settingsAtoms'
import { funeralHomeDateFormat } from '@/utils/dates/funeralHomeDateFormat'
import { sanitizeCaseDates } from '@/utils/sanitizers/sanitizeCaseDates'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useDynamicStyles } from '@/hooks/utility/useDynamicStyles'
import { previewGlobal } from '@/styles/previewGlobal'
import type {
  BackgroundTypeKey,
  EditorOrderPageDetailsNormalized,
  Content,
  GetCaseByIdNormalized,
  GetCaseImageForRenderNormalized,
  GetFuneralHomeNormalized,
  GetFuneralHomeSettingNormalized,
  GetGlobalServiceInformationTemplatesNormalized,
  GetThemeNormalized,
} from '@messenger/types'
import { LOCALE } from '@messenger/consts'
import { pickCaseImageDisplayUrl } from '@messenger/utils'

type UsePdfRenderReturn = {
  pages: (Content | null)[]
  data: EditorOrderPageDetailsNormalized
  backgroundType: BackgroundTypeKey
  concatenatedPages: number
  contentDimensions: { width: number; height: number }
  scale: number
  // Phase 1: true once every dependency atom (dateFormat, case,
  // funeralHomeDetails, selectedTheme, serviceTemplates, caseImages,
  // pageSetLocale, logo, backgroundType) is populated AND pages have been queued. Surfaced to
  // the DOM as `data-pdf-context-ready` so the puppeteer wait loop can
  // gate screenshot/PDF capture on it.
  contextReady: boolean
}

export const usePdfRender = (): UsePdfRenderReturn => {
  const handleError = useErrorHandler('usePdfRender')

  // State
  const [pages, setPages] = useState<(Content | null)[]>([])
  const [data, setData] = useState<EditorOrderPageDetailsNormalized>(
    {} as EditorOrderPageDetailsNormalized,
  )
  const [backgroundType, setBackgroundType] =
    useState<BackgroundTypeKey>('PLAIN')
  const [concatenatedPages, setConcatenatedPages] = useState(0)
  const [contentDimensions, setContentDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [scale, setScale] = useState(1)
  const [contextReady, setContextReady] = useState(false)

  // Recoil setters
  const setFHLogo = useSetRecoilState(fhLogoAtom)
  const setDateFormat = useSetRecoilState(dateFormatAtom)
  const setCase = useSetRecoilState(caseSelector)
  const setFuneralHomeDetails = useSetRecoilState(caseFuneralHomeAtom)
  const setSelectedTheme = useSetRecoilState(
    selectedThemeAtom as RecoilState<GetThemeNormalized | null>,
  )
  const setPageSetLocale = useSetRecoilState(pageSetLocaleAtom)
  const setServiceTemplates = useSetRecoilState(serviceInformationTemplatesAtom)
  const setCaseImages = useSetRecoilState(caseImagesAtom)
  // The PDF surface renders without credentials, so it cannot fetch page
  // content: the rows ride the print payload and are seeded from there.
  const seedEditorPageContent = useRecoilCallback(
    ({ set }) =>
      (pageId: string, bundle: EditorPageContentBundle) =>
        set(editorPageContentAtomFamily(pageId), bundle),
    [],
  )

  useDynamicStyles(previewGlobal)

  // Calculate scale for letter paper size (8.5 x 11 inches) minus header and footer
  const calculateScale = useCallback(
    (pageWidth: number, pageHeight: number) => {
      const STANDARD_LETTER_PAPER_WIDTH_IN_INCHES = 8.5
      const STANDARD_LETTER_PAPER_HEIGHT_IN_INCHES = 11
      const STANDARD_SCREEN_DOTS_PER_INCH = 96
      const LETTER_PAPER_WIDTH_IN_PIXELS =
        STANDARD_LETTER_PAPER_WIDTH_IN_INCHES * STANDARD_SCREEN_DOTS_PER_INCH
      const LETTER_PAPER_HEIGHT_IN_PIXELS =
        STANDARD_LETTER_PAPER_HEIGHT_IN_INCHES * STANDARD_SCREEN_DOTS_PER_INCH
      const HEADER_SPACE_IN_INCHES = 0.8
      const FOOTER_SPACE_IN_INCHES = 1.2
      const HEADER_HEIGHT_IN_PIXELS =
        HEADER_SPACE_IN_INCHES * STANDARD_SCREEN_DOTS_PER_INCH
      const FOOTER_HEIGHT_IN_PIXELS =
        FOOTER_SPACE_IN_INCHES * STANDARD_SCREEN_DOTS_PER_INCH
      const availableHeight =
        LETTER_PAPER_HEIGHT_IN_PIXELS -
        HEADER_HEIGHT_IN_PIXELS -
        FOOTER_HEIGHT_IN_PIXELS
      const STANDARD_PAPER_EDGE_PADDING_IN_PIXELS = 32
      const availableWidth =
        LETTER_PAPER_WIDTH_IN_PIXELS - STANDARD_PAPER_EDGE_PADDING_IN_PIXELS
      const TRIM_MARK_VERTICAL_BUFFER_IN_PIXELS = 120
      const TRIM_MARK_HORIZONTAL_BUFFER_IN_PIXELS = 120
      const contentAreaHeight =
        availableHeight - TRIM_MARK_VERTICAL_BUFFER_IN_PIXELS
      const contentAreaWidth =
        availableWidth - TRIM_MARK_HORIZONTAL_BUFFER_IN_PIXELS
      const isLandscape = pageWidth > pageHeight

      if (isLandscape) {
        // For landscape: fit width, scale height proportionally
        return Math.min(
          contentAreaWidth / pageWidth,
          contentAreaHeight / pageHeight,
        )
      }
      // For portrait: fit height, scale width proportionally
      return Math.min(
        contentAreaHeight / pageHeight,
        contentAreaWidth / pageWidth,
      )
    },
    [],
  )

  const removeFeedbackTab = useMemo(() => {
    const addStyle = (styleString: string) => {
      const style = document.createElement('style')
      style.textContent = styleString
      document.head.append(style)
    }
    return () => {
      addStyle(`
        #bugmuncher_button {
          opacity: 0;
          pointer-events: none;
        }
      `)
    }
  }, [])

  const sendRequest = useCallback(async () => {
    try {
      // API request timeout in milliseconds (30 seconds)
      const PDF_PRINT_REQUEST_TIMEOUT_IN_MS = 30000

      const { data } = await axios.get(`pdfPrint`, {
        timeout: PDF_PRINT_REQUEST_TIMEOUT_IN_MS,
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      const prepareVisualDataForPdfCapture = (
        data: Omit<EditorOrderPageDetailsNormalized, 'case'> & {
          // The render payload carries `display_url` on each case image
          // (added server-side by `enrichEditorDataForRender`). It can be
          // missing: a backend deployed before the frontend, or a queued
          // screenshot job whose editor data was captured before it, sends
          // the rows without it.
          case: Omit<GetCaseByIdNormalized, 'images'> & {
            images?: (Omit<GetCaseImageForRenderNormalized, 'display_url'> & {
              display_url?: string | null
            })[]
          }
          funeral_home: GetFuneralHomeNormalized
          settings: GetFuneralHomeSettingNormalized
          logo: string | null // either small, medium, or large from GetFuneralHomeLogoNormalized
          service_templates: GetGlobalServiceInformationTemplatesNormalized[]
        },
      ) => {
        // Phase 1 invariant (#2482-followup): every dependency atom that
        // PageText.applyText reads (dateFormat, caseDetails,
        // funeralHomeDetails, selectedTheme, serviceTemplates, caseImages,
        // pageSetLocale, logo, backgroundType) must be set BEFORE setPages — otherwise the
        // first PageText render bakes default-format text into the DOM and
        // the screenshot path captures it before re-application can flush.
        // Order matters: atoms first, then pages last.

        // Process case data
        data.case = sanitizeCaseDates(data.case)

        // 1. Background + logo
        const backgroundType = data.original_layout?.background_type
        if (backgroundType) setBackgroundType(backgroundType)
        setFHLogo(data.logo)

        // 2. Case images
        const normalizedImages = data.case?.images
        if (normalizedImages?.length) {
          setCaseImages(
            normalizedImages.map((img) => ({
              id: img.id,
              originalUrl: img.original_url,
              mioUrl: img.mio_url ?? null,
              // Without a resolved URL the shared rule picks from the stored
              // copies (no variants ride the payload), so a slot never
              // prints blank.
              displayUrl:
                img.display_url ??
                pickCaseImageDisplayUrl(
                  {
                    mioUrl: img.mio_url ?? null,
                    originalUrl: img.original_url,
                  },
                  [],
                ),
              default: img.default ?? false,
              sortOrder: img.sort_order,
              uploadSource: img.upload_source ?? '',
            })),
          )
        }

        // 3. Case + funeral home + theme + service templates. The theme is
        // set here with the case, not left to ProductPrint's mount effect: a
        // TinyMCE box's dynamic-text merge does not track the theme. Any
        // merge uses whatever theme is current when it runs, but none is
        // guaranteed to run after a theme that lands later than the case,
        // so such a theme can miss the theme-dependent text (the M3 made_in
        // line, the theme font and colour of case-detail rows).
        setCase(data.case)
        setFuneralHomeDetails(data.funeral_home)
        if (!isEmpty(data.theme)) setSelectedTheme(data.theme)
        setServiceTemplates(data.service_templates)

        // 4. Date format + locale (PageText.applyText reads these)
        const format = funeralHomeDateFormat(
          data?.settings?.date_format || 'MMMM do yyyy',
        )
        setDateFormat(format)
        setPageSetLocale(data?.page_set?.language || LOCALE.EN)

        for (const page of data.pages) {
          if (page.json_template?.product)
            page.json_template.product.orderPageId = page.id
          if (typeof page.editor_page_content_json === 'string')
            seedEditorPageContent(
              page.id,
              JSON.parse(
                page.editor_page_content_json,
              ) as EditorPageContentBundle,
            )
        }

        // 5. Compute layout-derived state
        const DEFAULT_PAGE_NUMBER = 0
        const pageData = data.pages
          .map((page) => page.json_template)
          .sort(
            (a, b) =>
              (a?.pageProperties?.pageNumber ?? DEFAULT_PAGE_NUMBER) -
              (b?.pageProperties?.pageNumber ?? DEFAULT_PAGE_NUMBER),
          )

        const concatPages = pageData[0]?.pageProperties?.concatenatedPages
        if (concatPages) setConcatenatedPages(concatPages)

        // Standard letter size is 8.5" wide. At typical 96 DPI, this translates to ~816px
        // similar logic for the default page height of 11" in pixels
        const DEFAULT_PAGE_WIDTH_IN_PIXELS = 816
        const DEFAULT_PAGE_HEIGHT_IN_PIXELS = 1056

        const shouldCalculateContentDimensionsAndScale = pageData.length > 0
        if (shouldCalculateContentDimensionsAndScale) {
          const firstPage = pageData[0]
          const pageWidth =
            firstPage?.pageProperties?.pageSize?.width ||
            DEFAULT_PAGE_WIDTH_IN_PIXELS
          const pageHeight =
            firstPage?.pageProperties?.pageSize?.height ||
            DEFAULT_PAGE_HEIGHT_IN_PIXELS

          setContentDimensions({ width: pageWidth, height: pageHeight })

          const calculatedScale = calculateScale(pageWidth, pageHeight)
          setScale(calculatedScale)
        }

        // 6. Pages LAST. By the time PageText mounts and reads atoms, every
        // dependency above has been queued in the same React batch, so the
        // first render uses final values (not initial defaults).
        setData(data)
        setPages(pageData)

        // 7. Mark context ready AFTER pages queued. Wrapped in two
        // requestAnimationFrame to guarantee React commits + browser paints
        // both flush before the puppeteer wait loop sees the marker.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setContextReady(true)
          })
        })
      }

      prepareVisualDataForPdfCapture(data)
    } catch (error: unknown) {
      handleError(
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ||
          (error as Error).message,
        error as Error,
      )
    }
  }, [
    handleError,
    setCase,
    setCaseImages,
    setDateFormat,
    setFHLogo,
    setFuneralHomeDetails,
    setSelectedTheme,
    setPageSetLocale,
    calculateScale,
    setServiceTemplates,
    seedEditorPageContent,
  ])

  useEffect(() => {
    sendRequest()
    removeFeedbackTab()
    //eslint-disable-next-line
  }, [])

  return {
    pages,
    data,
    backgroundType,
    concatenatedPages,
    contentDimensions,
    scale,
    contextReady,
  }
}
